import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Hexagon, Loader2 } from "lucide-react";

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back to the hive.");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to buzz through your study day.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="login-email" required />
        <Field label="Password" type="password" value={password} onChange={setPassword} testid="login-password" required />
        <button type="submit" disabled={loading} data-testid="login-submit" className="bee-btn-primary w-full flex items-center justify-center gap-2">
          {loading ? <Loader2 className="animate-spin" size={16} /> : "Sign in"}
        </button>
      </form>
      <p className="mt-6 text-sm text-[color:var(--bee-muted)]">No account? <Link to="/register" className="text-[color:var(--bee-text)] font-semibold underline decoration-[color:var(--bee-primary)] underline-offset-4">Create one →</Link></p>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Profile created. Welcome to the hive!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Join the hive" subtitle="Free forever. Every subject, every level.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label="Your name" value={name} onChange={setName} testid="register-name" required />
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="register-email" required />
        <Field label="Password (min 6)" type="password" value={password} onChange={setPassword} testid="register-password" required minLength={6} />
        <button type="submit" disabled={loading} data-testid="register-submit" className="bee-btn-primary w-full flex items-center justify-center gap-2">
          {loading ? <Loader2 className="animate-spin" size={16} /> : "Create account"}
        </button>
      </form>
      <p className="mt-6 text-sm text-[color:var(--bee-muted)]">Already have an account? <Link to="/login" className="text-[color:var(--bee-text)] font-semibold underline decoration-[color:var(--bee-primary)] underline-offset-4">Sign in →</Link></p>
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 bee-honeycomb-bg">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 hex-clip bg-[color:var(--bee-primary)] flex items-center justify-center">
            <Hexagon size={20} className="text-[color:var(--bee-text)]" strokeWidth={2.5} />
          </div>
          <span className="font-display text-2xl font-bold">FocusBee</span>
        </div>
        <div className="bee-card p-8">
          <h1 className="font-display text-3xl font-bold tracking-tight">{title}</h1>
          <p className="text-[color:var(--bee-muted)] mt-2 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, required, minLength }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.1em] uppercase font-bold text-[color:var(--bee-muted)]">{label}</span>
      <input
        type={type}
        value={value}
        required={required}
        minLength={minLength}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="bee-input w-full mt-1.5"
      />
    </label>
  );
}
